import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  frequencyAnswerOption,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  symptomItem,
  withEs,
} from './scoredSurvey';

/**
 * PHQ-2 (Patient Health Questionnaire-2) depression screening instrument — the two-item short form
 * used as a first-pass screen before the full PHQ-9. Item structure and LOINC coding follow the
 * HL7 SDC PHQ-2 panel (LOINC 55757-9), published here under our own canonical `url` and version,
 * mirroring {@link ./phq9} — the two share the scored-survey machinery in {@link ./scoredSurvey}.
 *
 * Both items are required and share the standard 0–3 frequency scale ({@link frequencyAnswerOptions}).
 * The two items are the first two of PHQ-9 (LOINC 44250-9 and 44255-8) with identical wording,
 * translations, and answer options — so their strings are transcribed to match PHQ-9 verbatim.
 *
 * The total score (0–6) is derived server-side via Medplum's SDC `QuestionnaireResponse/$extract`:
 * a contained Observation template (see {@link buildScoreObservationTemplate}) carrying the
 * total-score LOINC plus standard `sdc-questionnaire-templateExtract` annotations. Behavior is
 * verified by `src/__tests__/integration/phq2ExtractScoring.test.ts`.
 *
 * Because PHQ-2 is a validated instrument, these strings must be copied exactly and never
 * free-translated; scoring validity depends on the exact wording. Per the official PHQ family:
 * "Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an
 * educational grant from Pfizer Inc. No permission required to reproduce, translate, display or
 * distribute."
 */

/** PHQ-2 total score. Distinct from the panel LOINC (55757-9) on `Questionnaire.code`. */
export const PHQ2_TOTAL_SCORE_LOINC = '55758-7';

const frequencyAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA6568-5', 'Not at all', 'Ningún día'),
  frequencyAnswerOption('1', 1, 'LA6569-3', 'Several days', 'Varios días'),
  frequencyAnswerOption('2', 2, 'LA6570-1', 'More than half the days', 'Más de la mitad de los días'),
  frequencyAnswerOption('3', 3, 'LA6571-9', 'Nearly every day', 'Casi todos los días'),
];

const scoredSymptomItems: QuestionnaireItemWithTranslations[] = [
  symptomItem(
    '/44250-9',
    '44250-9',
    'Little interest or pleasure in doing things',
    'Little interest or pleasure in doing things',
    'Poco interés o placer en hacer las cosas',
    true,
    frequencyAnswerOptions,
  ),
  symptomItem(
    '/44255-8',
    '44255-8',
    'Feeling down, depressed, or hopeless',
    'Feeling down, depressed, or hopeless',
    'Se ha sentido decaído(a), deprimido(a), o sin esperanzas',
    true,
    frequencyAnswerOptions,
  ),
];

const phq2ScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'phq2-score-tmpl',
  totalScoreCode: PHQ2_TOTAL_SCORE_LOINC,
  display: 'PHQ-2',
  scoredItems: scoredSymptomItems,
});

export const phq2Questionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('phq2-score-tmpl')],
  contained: [phq2ScoreObservationTemplate],
  url: 'https://liza.health/fhir/questionnaire/phq-2',
  identifier: [{ system: 'urn:ietf:rfc:3986', value: 'https://liza.health/fhir/questionnaire/phq-2' }],
  version: '1',
  name: 'PHQ2',
  language: 'en',
  title: 'PHQ-2',
  _title: withEs('Cuestionario sobre la salud del paciente-2 (PHQ-2)'),
  status: 'active',
  description: 'Over the last 2 weeks, how often have you been bothered by any of the following problems?',
  _description: withEs(
    'Durante las últimas 2 semanas, ¿qué tan seguido le han afectado cualquiera de los siguientes problemas?',
  ),
  subjectType: ['Patient'],
  date: '2026-06-24',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'This material contains content from LOINC® (http://loinc.org). LOINC is copyright © 1995-2024, Regenstrief Institute, Inc. and the LOINC Committee, and is available at no cost under the license at http://loinc.org/license. Copyright © Pfizer Inc. All rights reserved. Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues, with an educational grant from Pfizer Inc. No permission required to reproduce, translate, display or distribute (https://www.phqscreeners.com/).',
  code: [{ system: 'http://loinc.org', code: '55757-9', display: 'Patient health questionnaire 2 item (PHQ-2)' }],
  item: scoredSymptomItems,
};
